import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { icalAPI } from '../config/api';
import { FiRefreshCw, FiLink, FiCheck, FiAlertTriangle, FiArrowLeft, FiCopy, FiTrash2 } from 'react-icons/fi';

const ICalSync = () => {
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const [editingVilla, setEditingVilla] = useState(null);
  const [editUrl, setEditUrl] = useState('');
  const [syncStatus, setSyncStatus] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const stored = localStorage.getItem('user');
    if (!stored) { navigate('/login'); return; }
    const parsed = JSON.parse(stored);
    if (!parsed.is_admin) { navigate('/dashboard'); return; }
    fetchLinks();
    icalAPI.syncStatus().then(res => setSyncStatus(res.data)).catch(() => {});
  }, [navigate]);

  const fetchLinks = () => {
    setLoading(true);
    icalAPI.getLinks()
      .then(res => setLinks(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  const handleSyncAll = async () => {
    setSyncing(true);
    setSyncResult(null);
    try {
      const res = await icalAPI.syncAll();
      setSyncResult(res.data);
      fetchLinks();
    } catch {
      setSyncResult({ error: 'Sync failed' });
    } finally {
      setSyncing(false);
    }
  };

  const handleSyncVilla = async (villaId) => {
    setSyncing(true);
    try {
      await icalAPI.syncVilla(villaId);
      fetchLinks();
    } catch {}
    setSyncing(false);
  };

  const handleUpdateLink = async (villaId) => {
    try {
      await icalAPI.updateLink(villaId, editUrl);
      setEditingVilla(null);
      setEditUrl('');
      fetchLinks();
    } catch {}
  };

  const handleClearBlocks = async (villaId) => {
    if (!window.confirm('Clear all Airbnb-synced blocked dates for this villa?')) return;
    try {
      await icalAPI.clearAirbnbBlocks(villaId);
      fetchLinks();
    } catch {}
  };

  const copyExportUrl = (path) => {
    const url = `${window.location.origin}${path}`;
    navigator.clipboard.writeText(url);
  };

  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

  return (
    <div data-testid="ical-sync-page" className="pt-20 min-h-screen">
      <section className="py-12 md:py-16">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          {/* Header */}
          <Link to="/admin" className="text-[#F57C20] text-xs flex items-center gap-1 mb-6 hover:underline">
            <FiArrowLeft className="w-3 h-3" /> Back to Admin Dashboard
          </Link>

          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <p className="label-uppercase mb-2">Calendar Management</p>
              <h1 className="text-3xl font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                iCal <span className="text-[#F57C20]">Sync</span>
              </h1>
              <p className="text-white/50 text-sm mt-1">2-way calendar sync with Airbnb</p>
              {syncStatus && (
                <div data-testid="auto-sync-badge" className="flex items-center gap-2 mt-2">
                  <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                  <span className="text-green-400 text-xs font-medium">Auto-sync active · {syncStatus.interval_display} · {syncStatus.total_airbnb_blocks} blocks synced</span>
                </div>
              )}
            </div>
            <button
              onClick={handleSyncAll}
              disabled={syncing}
              data-testid="sync-all-btn"
              className="bg-[#F57C20] text-white px-6 py-3 text-xs font-semibold tracking-wider uppercase rounded-lg flex items-center gap-2 hover:bg-[#E06A10] transition-all disabled:opacity-50"
            >
              <FiRefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} />
              {syncing ? 'Syncing...' : 'Sync All from Airbnb'}
            </button>
          </div>

          {/* Sync Result */}
          {syncResult && (
            <div data-testid="sync-result" className="glass-subtle p-4 mb-6 border border-[#F57C20]/20">
              {syncResult.error ? (
                <div className="flex items-center gap-2 text-red-400 text-sm">
                  <FiAlertTriangle className="w-4 h-4" /> {syncResult.error}
                </div>
              ) : (
                <div>
                  <p className="text-white/80 text-sm mb-2">Sync completed successfully:</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {syncResult.results?.map((r, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        {r.status === 'success' ? (
                          <FiCheck className="w-3 h-3 text-green-400" />
                        ) : (
                          <FiAlertTriangle className="w-3 h-3 text-red-400" />
                        )}
                        <span className="text-white/60">{r.villa_id}:</span>
                        <span className="text-white/80">
                          {r.status === 'success' ? `${r.events_found} events found, ${r.events_added} new` : r.error}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* How it works */}
          <div className="glass-subtle p-6 mb-8 border border-white/5">
            <h3 className="text-white/80 text-sm font-semibold mb-3">How 2-Way Sync Works</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-white/50">
              <div>
                <p className="text-[#F57C20] font-semibold mb-1">Airbnb → Our System (Import)</p>
                <p>Fetches Airbnb iCal feed and blocks those dates on our calendar. Click "Sync" to pull latest bookings.</p>
              </div>
              <div>
                <p className="text-[#F57C20] font-semibold mb-1">Our System → Airbnb (Export)</p>
                <p>Copy the Export URL and paste it into Airbnb's "Import Calendar" to push our bookings to Airbnb.</p>
              </div>
            </div>
          </div>

          {/* Villa Cards */}
          {loading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4].map(i => <div key={i} className="shimmer h-40" />)}
            </div>
          ) : (
            <div className="space-y-4">
              {links.map(link => (
                <div key={link.villa_id} data-testid={`ical-card-${link.villa_id}`} className="glass-subtle p-6 border border-white/5">
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-4">
                    <div>
                      <h3 className="text-white text-lg font-light" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {link.villa_name}
                      </h3>
                      <p className="text-white/40 text-xs mt-1">
                        {link.airbnb_synced_blocks} Airbnb blocks synced
                        {link.last_synced_at && ` · Last sync: ${new Date(link.last_synced_at).toLocaleString()}`}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleSyncVilla(link.villa_id)}
                        disabled={syncing}
                        data-testid={`sync-${link.villa_id}`}
                        className="border border-[#F57C20]/40 text-[#F57C20] px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 hover:bg-[#F57C20] hover:text-white transition-all disabled:opacity-50"
                      >
                        <FiRefreshCw className={`w-3 h-3 ${syncing ? 'animate-spin' : ''}`} /> Sync
                      </button>
                      <button
                        onClick={() => handleClearBlocks(link.villa_id)}
                        data-testid={`clear-${link.villa_id}`}
                        className="border border-red-500/30 text-red-400 px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 hover:bg-red-500 hover:text-white transition-all"
                      >
                        <FiTrash2 className="w-3 h-3" /> Clear
                      </button>
                    </div>
                  </div>

                  {/* Import URL */}
                  <div className="mb-3">
                    <label className="text-white/40 text-[10px] uppercase tracking-wider font-semibold block mb-1">
                      Airbnb Import URL (their calendar → ours)
                    </label>
                    {editingVilla === link.villa_id ? (
                      <div className="flex gap-2">
                        <input
                          value={editUrl}
                          onChange={e => setEditUrl(e.target.value)}
                          placeholder="https://www.airbnb.com/calendar/ical/..."
                          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white text-xs focus:outline-none focus:border-[#F57C20]"
                        />
                        <button onClick={() => handleUpdateLink(link.villa_id)} className="bg-[#F57C20] text-white px-3 py-2 text-xs rounded-lg">Save</button>
                        <button onClick={() => setEditingVilla(null)} className="text-white/40 text-xs">Cancel</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/60 text-xs truncate">
                          {link.airbnb_import_url || 'Not configured'}
                        </div>
                        <button
                          onClick={() => { setEditingVilla(link.villa_id); setEditUrl(link.airbnb_import_url || ''); }}
                          className="text-[#F57C20] text-xs hover:underline flex-shrink-0"
                        >
                          Edit
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Export URL */}
                  <div>
                    <label className="text-white/40 text-[10px] uppercase tracking-wider font-semibold block mb-1">
                      Export URL (ours → paste into Airbnb's "Import Calendar")
                    </label>
                    <div className="flex items-center gap-2">
                      <div className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white/60 text-xs truncate">
                        {API_BASE}{link.export_url}
                      </div>
                      <button
                        onClick={() => copyExportUrl(link.export_url)}
                        data-testid={`copy-export-${link.villa_id}`}
                        className="text-[#F57C20] text-xs flex items-center gap-1 hover:underline flex-shrink-0"
                      >
                        <FiCopy className="w-3 h-3" /> Copy
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default ICalSync;
